/* eslint-disable @typescript-eslint/camelcase */
import { AxiosInstance, AxiosRequestConfig } from "axios";
import { isEmpty, throttle } from "lodash";
import {
  DetailsResponse,
  StateResponse,
  RosterResponse,
  MeetingActivityKind,
  MyChatEntry,
  Gipt,
  TncResponse,
  MoreDetailsResponseSetting,
} from "@/interfaces/meeting/details";
import type { CheckList } from "@/interfaces/meeting/details"
import { AttendMeetingResponse } from "@/interfaces/meeting/conference";

export const getDetails = async (
  client: AxiosInstance
): Promise<DetailsResponse> => {
  const data = (await client.get<DetailsResponse>("meeting_details")).data;
  data.assets = data.assets || [];
  if (isEmpty(data.gipt_schema)) {
    data["gipt_schema"] = {};
  }
  if (isEmpty(data.gipt_context_properties)) {
    data["gipt_context_properties"] = [];
  }
  if(isEmpty(data.activation_meeting)) data.activation_meeting = "NONE";
  return data;
};

export const getMoreDetails = async (
  client: AxiosInstance,
  eventId: number
): Promise<MoreDetailsResponseSetting> => {
  const newBaseURL = process.env.VUE_APP_BOOST_NOW_NEW_BASE_URL
  const config: AxiosRequestConfig =  {
    params: {
      event: eventId,
      keys: 'BOOST_NOW_'
    }
  }
  if (newBaseURL) {
    config.baseURL = `${newBaseURL}/admin/boost_now/`
  }
  console.debug('config', config)
  const data = (await client.get<{ settings: MoreDetailsResponseSetting}>("../../administration/events/event_settings", config)).data;
  return data.settings;
};

export const attendMeeting = async (client: AxiosInstance) =>
  (await client.get<AttendMeetingResponse>("attend_meeting")).data;

export const loadMeetingState = async <T = StateResponse>(
  client: AxiosInstance,
  parts: string | string[] = ""
) => {
  const config: AxiosRequestConfig = {};
  if (parts) {
    if (typeof parts === "string") {
      config["params"] = { parts: [parts] };
    } else {
      config["params"] = { parts: parts };
    }
  }
  const response = await client.get<T>("meeting_state/", config);
  return response.data;
};

export const sendActivity = async (
  client: AxiosInstance,
  kind: MeetingActivityKind,
  comment: string,
  extra: Record<string, unknown> = {}
) => await client.post("meeting_activity", { kind, comment, extra });

export const recordMeetingState = async (
  client: AxiosInstance,
  data: any,
  part: keyof StateResponse | "" = ""
) =>
  (await client.post<StateResponse>(`meeting_state/record/${part}`, data)).data;

export const sendMessage = async (client: AxiosInstance, data: MyChatEntry) =>
  (await client.post<StateResponse>(`meeting_state/add_chat`, data)).data;

export const updateName = async (
  client: AxiosInstance,
  data: { userId: string; name: string }
) =>
  (await client.post<RosterResponse>("meeting_state/update_name", data)).data;

export const updateChecklist = throttle(async (
  client: AxiosInstance,
  data: CheckList
) =>
  (await client.post<Pick<StateResponse, "checklist">>("meeting_state/checklist", { checklist: data })).data, 5e3);


export const updateTncAccepted = async (
  client: AxiosInstance,
  direction: boolean | null
) =>
  (await client.post<TncResponse>("meeting_state/tnc_accepted", { tncAccepted: direction })).data;


export const isChecklistComplete = async (
  client: AxiosInstance
) =>
  (await client.get<{ complete: boolean }>("meeting_state/checklist/complete")).data;

export const getNames = async (client: AxiosInstance) =>
  (await client.get<RosterResponse>("meeting_state/")).data;

export const getUrlPreviewImageSrc = (
  client: AxiosInstance,
  url: string
): string => {
  const config: AxiosRequestConfig = {
    url: "preview",
    params: {
      token: client.defaults.headers["X-Itsdangerous"],
      url
    }
  };
  return `${client.defaults.baseURL}${client.getUri(config)}`;
};

export const sendSignatory = async (
  client: AxiosInstance,
  data: { signatory: string }
) => (await client.post<RosterResponse>("meeting_state/signatory", data)).data;

export const submitSignature = async (
  client: AxiosInstance,
  signature: string
) => await client.post<StateResponse>("meeting_state/signature", { signature });

export const placeOrder = async (
  client: AxiosInstance,
  confirmed: boolean,
  isContract = false,
  extraContext?: any
) => await client.post("../../orders/place_order", { confirmed, isContract, extra_context: extraContext });

export const setActivationMeeting = async (
  client: AxiosInstance,
  activationDateTime: [string, string],
  timezone: string,
  extraRecipients: string[] = [],
  location = ''
) => await client.post("../../orders/activation_meeting", {timezone, date: activationDateTime[0], time: activationDateTime[1], extraRecipients, location})

export const updateGiptForm = async (client: AxiosInstance, gipt: Gipt) =>
  await client.post<StateResponse>("meeting_state/gipt", { gipt });

export const updateGiptPackages = async (client: AxiosInstance, gipt: Gipt) =>
  (
    await client.post<StateResponse>("meeting_state/update_gipt_packages", {
      gipt
    })
  ).data;

export const sendAnalytics = async (client: AxiosInstance, data: any) =>
  await client.post("meeting_analytics", data);
